import { QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { DirectoryRecord } from "@/shared/domain/files";
import {
  DirectoryNode,
  FileSystemNodeType,
  isDirectory,
} from "@/shared/domain/files/filesystem";

import { fileQueryKeys } from "./fileQueryKeys";
import {
  baseName,
  cancelAndSnapshotDirectoryContents,
  DirectoryContentsCache,
  dirPath,
  restoreDirectoryContentsSnapshot,
  transformDirectoryContentsCache,
} from "./moveDirectoryContentsCache";

type MoveContext = {
  snapshot: [QueryKey, DirectoryContentsCache | undefined][];
};

interface MoveDirectoryProps {
  associationId: string;
  /** Full relative path of the directory being moved (e.g. "parent/subdir"). */
  currentPath: string;
  newFullPath: string;
}

export function useMoveDirectoryMutation() {
  const { files: fileService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<DirectoryRecord, Error, MoveDirectoryProps, MoveContext>({
    mutationFn: ({ associationId, currentPath, newFullPath }) => {
      return fileService.moveDirectory(associationId, currentPath, newFullPath);
    },
    onMutate: async ({ associationId, currentPath, newFullPath }) => {
      // The directory node appears in its *parent* directory's listing
      const sourceDir = dirPath(currentPath);
      const destDir = dirPath(newFullPath);

      const snapshot = await cancelAndSnapshotDirectoryContents(
        queryClient,
        associationId,
        [sourceDir, destDir],
      );

      let movedNode: DirectoryNode | undefined;
      transformDirectoryContentsCache(
        queryClient,
        associationId,
        sourceDir,
        (items) =>
          items.filter((n) => {
            if (isDirectory(n) && n.directory.relative_path === currentPath) {
              movedNode = n;
              return false;
            }
            return true;
          }),
      );

      if (movedNode && sourceDir !== destDir) {
        const newName = baseName(newFullPath);
        const optimisticNode: DirectoryNode = {
          type: FileSystemNodeType.Directory,
          name: newName,
          directory: {
            ...movedNode.directory,
            relative_path: newFullPath,
            name: newName,
          },
        };
        transformDirectoryContentsCache(
          queryClient,
          associationId,
          destDir,
          (items) => [...items, optimisticNode],
        );
      }

      return { snapshot };
    },
    onError: (_err, _vars, context) => {
      if (context?.snapshot) {
        restoreDirectoryContentsSnapshot(queryClient, context.snapshot);
      }
    },
    onSettled: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: fileQueryKeys.directoryItemCount(),
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: fileQueryKeys.directoryContents(),
          exact: false,
        }),
      ]);
    },
  });
}
